/**
 * 판정 기록 목록 organism — 행 배지·약품명·미판정·발화 룰 수·정오 진행 + 로딩/오류/빈 2종.
 *
 * @spec:2026-09-29-처방검토-판정기록-조회화면
 *
 * 프리젠테이션 — 상태는 prop(페이지가 훅으로 판정). 도메인(필터 URL)을 모른다. 행 링크는 rowHref 주입.
 */
import React from "react";
import Tag from "components/atoms/Tag";
import Button from "components/atoms/Button";
import Spinner from "components/atoms/Spinner";
import type { ColorSystem } from "components/design/design-system";
import type { DoseReviewLogListItem, DoseGrade } from "services/api/doseReviewLogs/types";
import {
  gradeLabel,
  sourceLabel,
  productsSummary,
  unevaluatedText,
  labelProgressCell,
  DUP_INGREDIENT_BADGE,
  PENDING_DOWNGRADE_BADGE,
  PENDING_DOWNGRADE_TOOLTIP,
  EMPTY_NO_RECORDS_TITLE,
  EMPTY_NO_RECORDS_HINT,
  EMPTY_NO_MATCH_TITLE,
  LIST_ERROR_TEXT,
  RETRY_LABEL,
  RESET_FILTER_LABEL,
} from "utils/doseReviewLogs";
import { Wrap, RowLink, Cell, NameCell, BadgeRow, SubText, StateBox, HintText } from "./style";

const gradeTone = (g: DoseGrade | null): ColorSystem => {
  switch (g) {
    case "critical":
      return "Red";
    case "caution":
    case "warning":
      return "Yellow";
    case "appropriate":
      return "Blue";
    default:
      return "Gray";
  }
};

export interface DoseReviewLogListSectionProps {
  isLoading: boolean;
  isError: boolean;
  /** 오류 문구 — HTTP 400이면 페이지가 "입력값 오류"를 넘긴다(L1260). 기본은 일반 오류. */
  errorText?: string;
  onRetry?: () => void;
  items: DoseReviewLogListItem[];
  total: number;
  /** 빈 상태 구분 — 페이지가 hasNonPeriodFilter로 판정(D-12). */
  emptyVariant: "no-records" | "no-match";
  onResetFilter?: () => void;
  rowHref: (item: DoseReviewLogListItem) => string;
  /** 좌클릭 SPA 이동(풀 리로드 방지). 없으면 href 기본 동작. cmd/ctrl·새 탭은 href를 그대로 쓴다. */
  onRowClick?: (item: DoseReviewLogListItem) => void;
}

const DoseReviewLogListSection: React.FC<DoseReviewLogListSectionProps> = ({
  isLoading,
  isError,
  errorText,
  onRetry,
  items,
  total,
  emptyVariant,
  onResetFilter,
  rowHref,
  onRowClick,
}) => {
  if (isError) {
    return (
      <StateBox data-testid="dose-log-list-error">
        <div>{errorText ?? LIST_ERROR_TEXT}</div>
        {onRetry && (
          <Button buttonType="button" type="Text" size="Small" variant="Outlined" color="Secondary" onClick={onRetry}>
            {RETRY_LABEL}
          </Button>
        )}
      </StateBox>
    );
  }

  if (isLoading && items.length === 0) {
    return (
      <StateBox data-testid="dose-log-list-loading">
        <Spinner />
      </StateBox>
    );
  }

  if (total === 0 && items.length === 0) {
    if (emptyVariant === "no-match") {
      return (
        <StateBox data-testid="dose-log-empty-no-match">
          <div>{EMPTY_NO_MATCH_TITLE}</div>
          {onResetFilter && (
            <Button buttonType="button" type="Text" size="Small" variant="Outlined" color="Secondary" onClick={onResetFilter}>
              {RESET_FILTER_LABEL}
            </Button>
          )}
        </StateBox>
      );
    }
    return (
      <StateBox data-testid="dose-log-empty-no-records">
        <div>{EMPTY_NO_RECORDS_TITLE}</div>
        <HintText>{EMPTY_NO_RECORDS_HINT}</HintText>
      </StateBox>
    );
  }

  return (
    <Wrap>
      {items.map((item) => {
        const names = productsSummary(item.products);
        const alertRuleCount = item.alertRuleIds?.length ?? 0;
        return (
          <RowLink
            key={item.id}
            href={rowHref(item)}
            data-testid="dose-log-row"
            onClick={(e) => {
              // 좌클릭만 SPA 이동으로 가로채고, cmd/ctrl·중클릭·새 탭은 href 기본 동작(새 탭)을 둔다.
              if (onRowClick && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
                e.preventDefault();
                onRowClick(item);
              }
            }}
          >
            <Cell data-testid="dose-log-time">{String(item.requestedAt ?? "").slice(0, 16).replace("T", " ")}</Cell>
            <Cell>
              <Tag size="Small" variant="Tinted" color="Gray">
                {sourceLabel(item.source)}
              </Tag>
            </Cell>
            <Cell>
              <BadgeRow>
                <Tag size="Small" variant="Tinted" color={gradeTone(item.grade)}>
                  {gradeLabel(item.grade)}
                </Tag>
                {(item.dupIngredientCount ?? 0) > 0 && (
                  <Tag size="Small" variant="Tinted" color="Red">
                    {DUP_INGREDIENT_BADGE}
                  </Tag>
                )}
                {(item.pendingDowngradeCount ?? 0) > 0 && (
                  <span title={PENDING_DOWNGRADE_TOOLTIP}>
                    <Tag size="Small" variant="Outlined" color="Gray">
                      {PENDING_DOWNGRADE_BADGE}
                    </Tag>
                  </span>
                )}
              </BadgeRow>
            </Cell>
            <NameCell title={names.full} data-testid="dose-log-products">
              {names.text}
            </NameCell>
            <Cell data-testid="dose-log-count">
              약 {item.itemCount}개
              {item.unevaluatedCount > 0 && <SubText> · {unevaluatedText(item.unevaluatedCount)}</SubText>}
            </Cell>
            <Cell>
              <SubText data-testid="dose-log-alert-rules">발화 룰 {alertRuleCount}</SubText>
              {" · "}
              <SubText data-testid="dose-log-label-progress">
                정오 {labelProgressCell(item.labeledCount, item.labelableCount)}
              </SubText>
            </Cell>
          </RowLink>
        );
      })}
    </Wrap>
  );
};

export default DoseReviewLogListSection;
